import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import useLinkifiedLabel from './useLinkifiedLabel';

/** The `RadioWidget` is a widget for rendering a radio group.
 *  It is typically used with a string property constrained with enum options.
 *
 * @param props - The `WidgetProps` for this component
 */
export default function RadioWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>({
  id,
  schema,
  options,
  value,
  required,
  disabled,
  readonly,
  label,
  onChange,
  onBlur,
  onFocus,
}: WidgetProps<T, S, F>) {
  const { enumOptions, enumDisabled } = options;

  const _onChange = (_: any, val: any) => onChange(schema.type == 'boolean' ? val !== 'false' : val);
  const _onBlur = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onBlur(id, val);
  const _onFocus = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, val);

  const row = options ? options.inline : false;

  const displayLabel = useLinkifiedLabel(label);

  return (
    <>
      <FormLabel required={required} htmlFor={id}>
        {displayLabel || schema.title}
      </FormLabel>
      <RadioGroup id={id} name={id} value={`${value}`} row={row as boolean} onChange={_onChange} onBlur={_onBlur} onFocus={_onFocus}>
        {Array.isArray(enumOptions) &&
          enumOptions.map((option) => {
            const itemDisabled = Array.isArray(enumDisabled) && enumDisabled.indexOf(option.value) !== -1;
            return (
              <FormControlLabel
                control={<Radio name={id} id={`${id}-${option.value}`} color="primary" inputProps={{ 'data-testid': 'root_test_radio_field' }} />}
                label={`${option.label}`}
                value={`${option.value}`}
                key={option.value}
                disabled={disabled || itemDisabled || readonly}
              />
            );
          })}
      </RadioGroup>
    </>
  );
}
